import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { api } from "@/api";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { ProfitCards } from "@/components/ProfitPanel";

const rs = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const CARD_COLORS = [
  { bg: "#EEF2FF", bar: "#4F46E5", label: "#4338CA", value: "#312E81" },
  { bg: "#ECFDF5", bar: "#059669", label: "#047857", value: "#064E3B" },
  { bg: "#FFFBEB", bar: "#D97706", label: "#B45309", value: "#78350F" },
  { bg: "#FFF1F2", bar: "#E11D48", label: "#BE123C", value: "#881337" },
  { bg: "#F0FDFA", bar: "#0D9488", label: "#0F766E", value: "#134E4A" },
  { bg: "#F5F3FF", bar: "#7C3AED", label: "#6D28D9", value: "#4C1D95" },
  { bg: "#F0F9FF", bar: "#0284C7", label: "#0369A1", value: "#0C4A6E" },
  { bg: "#FDF4FF", bar: "#C026D3", label: "#A21CAF", value: "#701A75" },
  { bg: "#FFF7ED", bar: "#EA580C", label: "#C2410C", value: "#7C2D12" },
];

const Stat = ({ label, value, testId, tone = 0 }) => {
  const c = CARD_COLORS[tone % CARD_COLORS.length];
  return (
    <div
      data-testid={testId}
      className="overflow-hidden rounded-sm border transition-transform hover:-translate-y-0.5"
      style={{ background: c.bg, borderColor: `${c.bar}40` }}
    >
      <div className="h-1 w-full" style={{ background: c.bar }} />
      <div className="p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: c.label }}>{label}</p>
        <p className="font-mono-num mt-2 text-2xl font-semibold" style={{ color: c.value }}>{value}</p>
      </div>
    </div>
  );
};

export default function AdminDashboard() {
  const [profile, setProfile] = useState(null);
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/profile").then(({ data }) => setProfile(data)).catch(() => {});
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <AppShell profile={profile}>
      <main className="mx-auto max-w-[1500px] px-4 py-8 sm:px-8">
        <div className="mod-band rounded-sm px-5 py-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#4338CA" }}>Dashboard</p>
          <h1 className="font-head mt-2 text-4xl font-extrabold tracking-tighter sm:text-5xl">
            {profile?.company_name || "AGARWAL360"}
          </h1>
        </div>

        {!data ? (
          <p className="py-16 text-center text-sm text-zinc-500">Loading…</p>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="dashboard-stats">
              <Stat tone={0} testId="stat-sales-today" label="Sales today" value={rs(data.sales_today)} />
              <Stat tone={1} testId="stat-sales-month" label="Sales this month" value={rs(data.sales_month)} />
              <Stat tone={2} testId="stat-purchase-month" label="Purchase this month" value={rs(data.purchase_month)} />
              <Stat tone={3} testId="stat-outstanding" label="Outstanding" value={rs(data.outstanding)} />
              <Stat tone={4} testId="stat-stock-value" label="Stock value (cost)" value={rs(data.stock_value)} />
              <Stat tone={5} testId="stat-stock-sales-value" label="Stock value (sales)" value={rs(data.stock_sales_value)} />
              <Stat tone={6} testId="stat-items" label="Items in stock" value={data.items} />
              <Stat tone={7} testId="stat-pending-so" label="Pending sales orders" value={data.pending_sales_orders} />
              <Stat tone={8} testId="stat-pending-po" label="Pending purchase orders" value={data.pending_purchase_orders} />
            </div>

            {data.profit && <section className="mt-8">
              <h2 className="font-head text-lg font-bold uppercase tracking-tight">Profitability (after hisab discount)</h2>
              <p className="mt-1 text-xs text-zinc-500">
                Net profit = taxable sale value − hisab extra discount − (meters × purchase rate from Item Master)
              </p>
              <div className="mt-4">
                <ProfitCards t={data.profit} prefix="dash-profit" />
              </div>
              {data.profit_by_company?.length > 0 && (
                <div className="mt-4 border border-zinc-200 bg-white p-5" data-testid="dash-profit-by-company">
                  <h3 className="font-head text-base font-bold uppercase tracking-tight">Company wise net profit</h3>
                  <div className="mt-3 divide-y divide-zinc-100">
                    {data.profit_by_company.map((r, idx) => (
                      <div key={`${r.label}-${idx}`} className="flex items-center justify-between gap-3 py-2">
                        <span className="text-sm font-semibold uppercase">{r.label}</span>
                        <span className={`font-mono-num text-sm ${r.net_profit < 0 ? "text-red-600" : "text-emerald-700"}`}>
                          {rs(r.net_profit)} · {r.margin_pct}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>}

            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className="border border-zinc-200 bg-white p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-head text-lg font-bold uppercase tracking-tight">Top customers</h2>
                  <button
                    data-testid="dashboard-customers-link"
                    className="flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-zinc-500 hover:text-zinc-950"
                    onClick={() => navigate("/customers")}
                  >
                    Customer dashboard <ArrowUpRight size={13} />
                  </button>
                </div>
                {data.top_customers.length === 0 ? (
                  <p className="mt-3 text-sm text-zinc-500">No sales yet.</p>
                ) : (
                  <div className="mt-3 divide-y divide-zinc-100">
                    {data.top_customers.map((c) => (
                      <button
                        key={c.customer}
                        data-testid={`dashboard-customer-${c.customer}`}
                        onClick={() => navigate(`/customers?name=${encodeURIComponent(c.customer)}`)}
                        className="flex w-full items-center justify-between gap-3 py-2 text-left hover:bg-zinc-50"
                      >
                        <span className="text-sm font-semibold uppercase">{c.customer}</span>
                        <span className="font-mono-num text-sm">{rs(c.amount)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="border border-zinc-200 bg-white p-5">
                <h2 className="font-head flex items-center gap-2 text-lg font-bold uppercase tracking-tight">
                  <AlertTriangle size={16} className="text-amber-600" /> Low stock ({data.low_stock_count})
                </h2>
                {data.low_stock.length === 0 ? (
                  <p className="mt-3 text-sm text-zinc-500">Everything is above its minimum.</p>
                ) : (
                  <div className="mt-3 divide-y divide-zinc-100" data-testid="dashboard-low-stock">
                    {data.low_stock.map((r, idx) => (
                      <div key={`${r.quality_name}-${r.shade_number}-${idx}`} className="flex items-center justify-between gap-3 py-2">
                        <span className="text-sm">
                          <span className="font-semibold uppercase">{r.quality_name}</span>
                          <span className="text-zinc-500"> · shade {r.shade_number}</span>
                        </span>
                        <span className="font-mono-num text-sm text-red-600">
                          {r.than_stock} / {r.min_stock} than
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </AppShell>
  );
}
